import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { EventItem } from '../../types/event';
import { WalkIcon, BookIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { useDetailCardTranslation } from '../../hooks/useDetailCardTranslation';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { isEmptyField } from '../../utils/timeUtils';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { getHotelItineraryUrl, getGoogleMapsPlaceUrl } from '../../utils/itineraryUtils';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';
import { useSection } from '../../hooks/useSection';
import { useAnalytics } from '../../hooks/useAnalytics';

interface DetailCardProps {
  item: EventItem;
  items: EventItem[];
  onClose: () => void;
  onItemChange: (item: EventItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: any;
  onShowPlace?: (place: any) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior,
  showIndex,
  place,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const { trackItemAction, trackEvent } = useAnalytics({
    sectionId: section?.id,
    sectionName: section?.names?.fr,
    sectionType: 'event'
  });

  React.useEffect(() => {
    const openedAt = Date.now();
    trackEvent('content_view', 'detail_card_view', {
      item_id: item.id,
      item_name: item.name,
      category: item.category,
      type: item.type,
      date: item.date,
      time: item.hour,
      source: 'detail_card'
    });
    return () => {
      const duration = Math.round((Date.now() - openedAt) / 1000);
      trackEvent('content_view', 'detail_card_close', {
        item_id: item.id,
        item_name: item.name,
        category: item.category,
        type: item.type,
        duration_seconds: duration
      });
    };
  }, [item.id]);

  const imageUrl = item.image_metadata?.url || item.image;
  const { displayImage, shouldDisplay } = useImageDisplay(imageUrl, 'detailCard');

  const { getDescription, getOffer, translatingDescription, descriptionError } = useDetailCardTranslation({
    itemId: item.id,
    itemName: item.name,
    description: item.description,
    offer: item.offer,
    descriptions: item.descriptions,
    offers: item.offers,
    tableId,
    sectionType: 'event'
  });

  const { isFavorite, handleFavoriteToggle } = useFavoriteToggle({
    itemId: item.id,
    itemName: item.name,
    category: item.category,
    type: item.type,
    sectionType: 'event',
    itemData: { ...item, image: imageUrl }
  });

  const otherDates = React.useMemo(() => {
    if (!item.idShow && !item.idshow) return [];
    const idShow = item.idShow || item.idshow;
    return items
      .filter(event =>
        (event.idShow === idShow || event.idshow === idShow) &&
        event.id !== item.id
      )
      .sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
      });
  }, [items, item]);

  const renderContent = (item: EventItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      <div className="w-full">{item.place}</div>

      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> &bull; </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>

      <div className="flex flex-wrap gap-2">
        {!isEmptyField(item.distance) && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        {!isEmptyField(item.timewalk) && (
          <div className="flex items-center">
            <WalkIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
          </div>
        )}
        {!isEmptyField(item.timebike) && (
          <div className="flex items-center">
            <BikeIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timebike, i18n.language)}</span>
          </div>
        )}
        {!isEmptyField(item.timedrive) && (
          <div className="flex items-center">
            <Car size={15} className="mr-1" fill="currentColor" />
            <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {item.date && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              }).format(new Date(item.date))}
              {item.hour && item.end_time ? (
                ` ${formatTimeRange(item.hour, item.end_time, i18n.language)}`
              ) : item.hour && item.duration ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))} (${formatTravelTime(item.duration, i18n.language)})`
              ) : item.hour ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))}`
              ) : ''}
            </span>
          </div>
        )}
        {item.price && (
          <div className="flex items-center" style={{ color: config['major-color'] }}>
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: EventItem) => {
    if (!item.booking) return null;

    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
        onClick={() => {
          trackItemAction(item.id, 'booking_click', 'event', {
            item_name: item.name,
            url: item.booking,
            category: item.category,
            type: item.type
          });
        }}
      >
        <BookIcon size={18} className="mr-2" />
        {getTranslatedText(translations, 'btn_booking', i18n.language)}
      </a>
    );
  };

  const translate = (key: string) => getTranslatedText(translations, key, i18n.language);
  const translateCat = (key: string) => getTranslatedCategory(translations, key, i18n.language);

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={translateCat}
      getTranslatedText={translate}
      itineraryUrl={getHotelItineraryUrl(item.fulladress, config.fulladress, (item as any).google_map, config.google_place_id)}
      googleMapsUrl={(item as any).google_map ? getGoogleMapsPlaceUrl((item as any).google_map) : undefined}
      description={getDescription()}
      offer={getOffer()}
      translating={translatingDescription}
      translationError={descriptionError}
      context={{
        sectionType: 'event',
        itemId: item.id,
        idShow: item.idShow || item.idshow,
        contentType: 'description'
      }}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
      otherDates={otherDates}
      onOtherDateClick={onItemChange}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      onShowPlace={onShowPlace}
    />
  );
};

export default DetailCard;
